/* eslint-disable @typescript-eslint/unbound-method */
/* eslint-disable @typescript-eslint/no-unsafe-return  */
/* eslint-disable @typescript-eslint/no-unsafe-member-access  */
/* eslint-disable @typescript-eslint/no-unsafe-argument  */

import * as d3 from 'd3';
import { interpolatePath } from 'd3-interpolate-path';

import {
  ExtendedPerformanceFilterRange,
  PerformanceFilterRange,
  PerformanceFilterRangeValue,
  PerformanceFilterTab,
  PerformanceGraphItem,
  PerformancePoint,
  PerformanceType
} from '@shared/models/Home';

export const createSvg = (selector: string, width: number, height: number) => {
  return d3
    .select(selector)
    .attr('width', width)
    .attr('height', height)
    .style('-webkit-tap-highlight-color', 'transparent')
    .style('overflow', 'visible');
};

export enum PerformanceColors {
  BALANCE = '--color-core-brand-5-5',
  RETURNS = '--color-core-brand-6-5',
  BENCHMARK = '--color-core-neutral-5',
  BACKGROUND = '--ion-color-light',
  EVENT = '--color-core-neutral-7',
  CORE_NEUTRAL_4 = '--color-core-neutral-4',
  CORE_BRAND_3_3 = '--color-core-brand-3-3'
}

export const defaultPerformanceFilters: PerformanceFilterTab[] = [
  { id: PerformanceFilterRange.WEEK, value: 'PERFORMANCE.performanceChart.dateRangeTabs.1week' },
  { id: PerformanceFilterRange.MONTH, value: 'PERFORMANCE.performanceChart.dateRangeTabs.1month' },
  { id: PerformanceFilterRange.HALF_YEAR, value: 'PERFORMANCE.performanceChart.dateRangeTabs.6month' },
  { id: PerformanceFilterRange.YEAR, value: 'PERFORMANCE.performanceChart.dateRangeTabs.1year' },
  { id: PerformanceFilterRange.CURRENT_YEAR, value: `${new Date().getFullYear()}` }
];

export const maxPerformanceFilter: PerformanceFilterTab = {
  id: PerformanceFilterRange.ALL,
  value: 'PERFORMANCE.performanceChart.dateRangeTabs.allTime'
};

export const portfolioUpdateFilter = (abbreviation = true): PerformanceFilterTab => {
  return {
    id: PerformanceFilterRange.LAST_PORTFOLIO_UPDATE,
    value: `PERFORMANCE.performanceChart.dateRangeTabs.lpu.${abbreviation ? 'short' : 'long'}`
  };
};

export const getModalPerformanceFilters = (availableAnnualFilters: number[]): PerformanceFilterTab[] => {
  return [
    portfolioUpdateFilter(false),
    ...availableAnnualFilters.map((year) => ({
      id: `${year}` as `${number}`,
      value: year
    })),
    maxPerformanceFilter
  ];
};

export const isAnnualPerformanceRange = (range: PerformanceFilterRangeValue): range is `${number}` => {
  return /^\d{4}$/.test(String(range));
};

export const isExtendedPerformanceRange = (range: PerformanceFilterRangeValue): range is ExtendedPerformanceFilterRange => {
  return range === PerformanceFilterRange.ALL || range === PerformanceFilterRange.LAST_PORTFOLIO_UPDATE || isAnnualPerformanceRange(range);
};

export const getExtendedPerformanceFilterTab = (range: ExtendedPerformanceFilterRange): PerformanceFilterTab => {
  if (range === PerformanceFilterRange.ALL) {
    return maxPerformanceFilter;
  }

  if (range === PerformanceFilterRange.LAST_PORTFOLIO_UPDATE) {
    return portfolioUpdateFilter();
  }

  return {
    id: range,
    value: Number(range)
  };
};

export const normalizePerformanceRange = <T>(range: T, availableAnnualFilters: number[]): T => {
  const selectedYear = Number(range);

  if (availableAnnualFilters.includes(selectedYear)) {
    return range;
  }

  const nextAvailableYear = [...availableAnnualFilters].sort((a, b) => a - b).find((year) => year > selectedYear);
  return nextAvailableYear ? (`${nextAvailableYear}` as T) : (PerformanceFilterRange.LAST_PORTFOLIO_UPDATE as T);
};

export const getAvailableAnnualFilters = (graphData: PerformancePoint[]): number[] => {
  if (graphData.length === 0) {
    return [];
  }

  const currentYear = new Date().getFullYear();
  const firstDataDate = graphData[0].date;
  const lastDataDate = graphData[graphData.length - 1].date;
  const pastYears: number[] = [];

  // eslint-disable-next-line radix
  for (let year = currentYear - 1; year >= parseInt(firstDataDate.split('-')[0], 10); year--) {
    const hasFullYearData = firstDataDate <= `${year}-01-01` && lastDataDate >= `${year}-12-31`;

    if (hasFullYearData) {
      pastYears.push(year);
    }

    if (pastYears.length === 5) {
      break;
    }
  }

  return pastYears;
};

export const getChartQuickFilters = (lastExtendedRange: ExtendedPerformanceFilterRange): PerformanceFilterTab[] => {
  return [...defaultPerformanceFilters, getExtendedPerformanceFilterTab(lastExtendedRange)];
};

export const getCssValueFromDom = (variableName: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(variableName);
};

export const getChartMaxMinValues = (data: PerformanceGraphItem[]): { min: number; max: number } => {
  return {
    min: d3.min(data, (d) => (typeof d.benchmarkValue === 'undefined' ? Math.min(d.value, d.moneyInOut) : Math.min(d.value, d.benchmarkValue))),
    max: d3.max(data, (d) => (typeof d.benchmarkValue === 'undefined' ? Math.max(d.value, d.moneyInOut) : Math.max(d.value, d.benchmarkValue)))
  };
};

export const getChartStartEndValues = (data: PerformanceGraphItem[]): { startItems: PerformanceGraphItem[]; endItems: PerformanceGraphItem[] } => {
  const firstItem = data[0];
  const lastItem = data[data.length - 1];

  const dayBeforeFirst = new Date(firstItem.date.getTime());
  const dayAfterLast = new Date(lastItem.date.getTime());

  dayBeforeFirst.setDate(firstItem.date.getDate() - 1);
  dayAfterLast.setDate(lastItem.date.getDate() + 1);

  return {
    startItems: [{ ...firstItem, date: dayBeforeFirst }, firstItem],
    endItems: [lastItem, { ...lastItem, date: dayAfterLast }]
  };
};

export const getXScale = (data: PerformanceGraphItem[], startValue: number, endValue: number) => {
  return d3
    .scaleTime()
    .nice()
    .range([startValue, endValue])
    .domain(d3.extent(data, (d) => d.date));
};

export const getYScale = (minValue: number, maxValue: number, svgHeight: number) => {
  return d3.scaleLinear().range([svgHeight, 0]).domain([minValue, maxValue]).nice();
};

export const getLine = (xScaleFn: (value: any) => any, yScaleFn: (value: any) => any, key: string, key2: string, lineCurve: string) => {
  return d3
    .line()
    .defined((d) => d[key])
    .x((d) => xScaleFn(d[key]))
    .y((d) => yScaleFn(d[key2]))
    .curve(d3[lineCurve]);
};

export const renderLine = (
  initialYValue: number,
  lineClass: string,
  svg,
  lineData,
  lineColor,
  lineColorUpdate: string | null = null,
  lineWidth,
  xScaleFn: (param) => any,
  yScaleFn: (param) => any,
  yKey: string,
  lineCurve = 'curveMonotoneX'
) => {
  const line = svg.selectAll(`.${lineClass}`).data([lineData]);
  const duration = 500;
  const t = d3.transition().duration(duration);

  return line.data([lineData]).join(
    (enter) => {
      enter
        .append('path')
        .attr('class', lineClass)
        .attr('fill', 'none')
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        .attr('stroke', `var(${lineColor})`)
        .attr('stroke-width', lineWidth)
        .attr(
          'd',
          d3
            .line()
            .x((d) => xScaleFn(d['date']))
            .y(() => yScaleFn(initialYValue))
            .curve(d3[lineCurve])
        )
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round')
        .transition(t)
        .attr('d', getLine(xScaleFn, yScaleFn, 'date', yKey, lineCurve));
    },
    (update) => {
      update.transition(t).attrTween('d', (d) => {
        let previous = '';
        try {
          previous = d3.select(`.${lineClass}`).attr('d');
        } catch (e) {
          previous = '';
        }
        const line = getLine(xScaleFn, yScaleFn, 'date', yKey, lineCurve);
        const current = line(d);
        return interpolatePath(previous, current);
      });
      if (lineColorUpdate !== null) {
        update.attr('stroke', `var(${lineColorUpdate})`);
      }
    },
    (exit) => exit.remove()
  );
};

export const getBisect = (mx, data: any[], xScaleFn: (param) => any) => {
  const bisectObj = d3.bisector((d: any) => d.date).left;
  // date value from xScale
  const date = (<any>xScaleFn).invert(mx);
  // get the object index from the data set for selected date
  const index = bisectObj(data, date, 1);
  // get the previous and current data item from
  const a = data[index - 1];
  const b = data[index];

  if (a?.date && b?.date) {
    return date - a.date > b.date - date ? b : a;
  }
  return {
    date: 0,
    value: 0,
    moneyInOut: 0,
    moneyReturn: 0,
    differenceToBenchmark: 0,
    benchmarkValue: 0,
    bondPercent: 0,
    cashPercent: 0,
    fundPercent: 0,
    stockPercent: 0,
    portfolioUpdate: null
  };
};

export const getClosestPortfolioUpdateItem = (
  xPosition: number,
  chartData: PerformanceGraphItem[],
  xScaleFn: (param) => any
): { item: PerformanceGraphItem; distance: number } | null => {
  return chartData
    .filter((item) => item.portfolioUpdate)
    .reduce<{ item: PerformanceGraphItem; distance: number } | null>((closest, item) => {
      const distance = Math.abs(xPosition - xScaleFn(item.date));

      if (!closest || distance < closest.distance) {
        return { item, distance };
      }

      return closest;
    }, null);
};

export const getPerformanceTooltip = (value: number, benchValue: number, differenceToBenchmark: number) => {
  const formattedValue = value / 100;
  const formattedBenchValue = benchValue / 100;
  const formattedDifferenceToBenchmark = differenceToBenchmark / 100;
  return [
    {
      percentage: formattedValue
    },
    {
      percentage: formattedBenchValue
    },
    {
      percentage: formattedDifferenceToBenchmark
    }
  ];
};

const tickFormat = (date: Date, language: string, numberOfDays: number) => {
  const DAYS_IN_HALF_YEAR = 180;
  let formatOptions: Intl.DateTimeFormatOptions = {};

  if (numberOfDays >= DAYS_IN_HALF_YEAR) {
    formatOptions = { month: 'short', year: 'numeric' };
  } else {
    formatOptions = { day: 'numeric', month: 'short' };
  }

  return new Intl.DateTimeFormat(language, formatOptions).format(date);
};

export const generateBottomAxis = (xScaleFn: (param) => any, chartItems: PerformanceGraphItem[], language: string) => {
  const chunkSize = Math.floor(chartItems.length / 2);

  return d3
    .axisBottom(xScaleFn as d3.AxisScale<any>)
    .tickValues([new Date(chartItems[0].date), new Date(chartItems[chunkSize].date), new Date(chartItems[chartItems.length - 1].date)])
    .tickFormat((d) => {
      return tickFormat(d, language, chartItems.length);
    });
};

export const createOrUpdateDotGroup = (
  svg: d3.Selection<d3.BaseType, unknown, HTMLElement, unknown>,
  groupClass: string,
  dotClass: string,
  backgroundColor: string,
  strokeColor: string,
  strokeWidth: number
): void => {
  const dot = svg.selectAll(`.${groupClass}`).data([0]);
  const dotGroup = dot.enter().append('g').attr('class', groupClass).style('opacity', 0);

  dotGroup.append('circle').attr('r', 8).attr('fill', backgroundColor);

  dotGroup
    .append('circle')
    .attr('class', dotClass)
    .attr('r', 4)
    .attr('stroke', strokeColor)
    .attr('stroke-width', strokeWidth)
    .attr('fill', backgroundColor);

  dot.select(`.${dotClass}`).attr('stroke', strokeColor);
  dot.raise();
};

export const performanceEventsMap = {
  [PerformanceFilterRange.WEEK]: '1_week',
  [PerformanceFilterRange.MONTH]: '1_month',
  [PerformanceFilterRange.HALF_YEAR]: '6_months',
  [PerformanceFilterRange.YEAR]: '1_year',
  [PerformanceFilterRange.ALL]: 'max',
  [PerformanceFilterRange.CURRENT_YEAR]: 'current_year',
  [PerformanceFilterRange.LAST_PORTFOLIO_UPDATE]: 'last_portfolio_update',
  [PerformanceType.BALANCE]: 'balance',
  [PerformanceType.PERFORMANCE]: 'performance'
};
